import { Apartment as ApartmentIcon, Edit as EditIcon } from '@mui/icons-material'
import { ListItemIcon } from '@mui/material'
import camelCase from 'lodash.camelcase'
import React, { useCallback, useMemo } from 'react'
import { MenuItemLink } from '@/helpers/compositions/MenuItemLink'
import { T } from '@/helpers/T'
import { useRoles } from '@/hooks/useRoles'
import { useSWRAxios } from '@/hooks/useSWRAxios'
import type { IEntitySimplified } from '@/modules/Entity/Entity'
import { useAuthContext } from '@/providers/Global'
import { NavbarContextSelector } from './NavbarContextSelector'

const NavbarGlobalEntityInternal: React.FC = () => {
  const Auth = useAuthContext()
  const { hasAuthority } = useRoles()

  const isAdmin = hasAuthority('ROLE_ADMIN')

  const entitiesSWR = useSWRAxios<{ data: IEntitySimplified[] }>(
    isAdmin ? '/api/entity?managedEntity=true&max=100' : false,
    { revalidateOnFocus: false },
    true,
  )

  const entities = useMemo<IEntitySimplified[]>(
    () => entitiesSWR.data?.data.data ?? [],
    [entitiesSWR.data],
  )

  const username = Auth.active?.username
  const current = Auth.active?.globalEntity

  const select = useCallback(
    (entity: IEntitySimplified) => {
      if (username) {
        Auth.updateGlobalEntity(username, entity)
      }
    },
    [Auth, username],
  )

  const editTo = current ? `/entities/${camelCase(current.clazz)}/${current.id}` : undefined

  if (!isAdmin || entities.length <= 1) {
    return null
  }

  const items = entities.map((entity) => ({ entity }))
  const label = current?.eapcID ?? current?.fullName ?? ''

  return (
    <NavbarContextSelector
      icon={<ApartmentIcon fontSize='small' />}
      label={label}
      menuHeader={<T id='c.global_entity_selector.menu_header' />}
      items={items}
      currentId={current?.id}
      onSelect={select}
      footer={
        editTo
          ? (close) => (
              <MenuItemLink to={editTo} onClick={close} sx={{ py: 1 }}>
                <ListItemIcon>
                  <EditIcon fontSize='small' />
                </ListItemIcon>
                <T id='lang.edit' />
              </MenuItemLink>
            )
          : undefined
      }
    />
  )
}

export const NavbarGlobalEntity = React.memo(
  NavbarGlobalEntityInternal,
) as typeof NavbarGlobalEntityInternal
